import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Download, ArrowLeft } from 'lucide-react';

const useTitle = () => {
  useEffect(() => {
    const prev = document.title;
    document.title = 'Logo preview · TMG';
    return () => { document.title = prev; };
  }, []);
};

// [file, label, bg = 'dark' | 'light' | 'checker']
const LOGOS = {
  'Horizontal lockup': [
    ['tmg-lockup-horizontal-dark.png', 'On black · white text', 'dark'],
    ['tmg-lockup-horizontal-light.png', 'On white · dark text', 'light'],
    ['tmg-lockup-horizontal-transparent-white.png', 'Transparent · white text', 'dark'],
    ['tmg-lockup-horizontal-transparent-dark.png', 'Transparent · dark text', 'light'],
  ],
  'Wordmark only (no TMG icon)': [
    ['tmg-wordmark-dark.png', 'On black', 'dark'],
    ['tmg-wordmark-light.png', 'On white', 'light'],
    ['tmg-wordmark-transparent-white.png', 'Transparent · white', 'dark'],
    ['tmg-wordmark-transparent-dark.png', 'Transparent · dark', 'light'],
  ],
  'Icon only (site-style outlined)': [
    ['tmg-icon-outlined-dark.png', 'On black', 'dark'],
    ['tmg-icon-outlined-transparent.png', 'Transparent (shown on checker)', 'checker'],
  ],
  'Stacked (icon on top, wordmark below)': [
    ['tmg-stacked-dark.png', 'On black', 'dark'],
    ['tmg-stacked-transparent-white.png', 'Transparent · white text', 'dark'],
  ],
  'Solid amber mark (avatar / favicon)': [
    ['tmg-logo-300.png', 'PNG · white bg', 'light'],
    ['tmg-logo-300-transparent.png', 'PNG · transparent', 'checker'],
    ['tmg-logo-300.jpg', 'JPEG', 'light'],
  ],
};

const bgClass = {
  dark: 'bg-black',
  light: 'bg-white',
  checker:
    'bg-[repeating-conic-gradient(#2a2a2f_0_25%,#1a1a1f_0_50%)] bg-[length:16px_16px]',
};

const LogoCard = ({ file, label, bg }) => (
  <div className="border border-white/10 bg-ink-900 overflow-hidden group">
    <div
      className={`h-56 flex items-center justify-center p-6 ${bgClass[bg]}`}
    >
      <img
        src={`/${file}`}
        alt={label}
        className="max-h-full max-w-full object-contain"
      />
    </div>
    <div className="p-4 flex items-start justify-between gap-3 border-t border-white/10">
      <div className="min-w-0">
        <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500">
          {label}
        </div>
        <div className="mt-1 font-mono text-[11px] text-amber truncate">
          /{file}
        </div>
      </div>
      <a
        href={`/${file}`}
        download
        data-testid={`dl-${file}`}
        className="inline-flex items-center gap-1.5 border border-white/15 text-zinc-300 hover:text-amber hover:border-amber px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.18em] flex-shrink-0"
      >
        <Download className="w-3 h-3" /> get
      </a>
    </div>
  </div>
);

const LogoPreview = () => {
  useTitle();
  return (
    <div className="min-h-screen bg-black text-white">
      {/* Top bar */}
      <div className="sticky top-0 z-40 border-b border-white/10 bg-black/80 backdrop-blur-xl">
        <div className="px-6 md:px-12 lg:px-16 h-14 flex items-center justify-between">
          <Link
            to="/links"
            data-testid="preview-back"
            className="font-mono text-[11px] uppercase tracking-[0.22em] text-zinc-400 hover:text-amber inline-flex items-center gap-2"
          >
            <ArrowLeft className="w-3 h-3" /> Back to /links
          </Link>
          <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500 hidden sm:inline">
            Logo preview
          </span>
        </div>
      </div>

      <section className="px-6 md:px-12 lg:px-16 pt-16 md:pt-20 pb-8 md:pb-10 border-b border-white/10">
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber mb-3">
          For visual approval
        </div>
        <h1 className="font-display text-[36px] sm:text-5xl md:text-6xl text-balance max-w-3xl leading-[1.05]">
          Pick the logos you want to keep.
        </h1>
        <p className="mt-6 max-w-3xl text-base text-zinc-300 leading-relaxed">
          Every variant, on the background it&apos;s meant for. Right-click any
          card to save, or click <span className="font-mono text-amber">get</span> to
          download.
        </p>
      </section>

      {Object.entries(LOGOS).map(([group, items]) => (
        <section
          key={group}
          className="px-6 md:px-12 lg:px-16 py-10 md:py-14 border-b border-white/10"
        >
          <h2 className="font-display text-xl md:text-2xl text-white mb-6">
            {group}
            <span className="ml-3 font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">
              {items.length} option{items.length > 1 ? 's' : ''}
            </span>
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {items.map(([file, label, bg]) => (
              <LogoCard key={file} file={file} label={label} bg={bg} />
            ))}
          </div>
        </section>
      ))}

      <section className="px-6 md:px-12 lg:px-16 py-10 md:py-16">
        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">
          Reply with the filenames you want to keep · I&apos;ll remove the rest and
          make the winners the canonical brand assets.
        </p>
      </section>
    </div>
  );
};

export default LogoPreview;
